import { Tooltip } from '@bugbug/core/components/Tooltip/RefactoredTooltip';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { OrganizationSubscription } from '@bugbug/core/types/organizations';
import type {
  ParallelSubscriptionAddonPlan,
  SubscriptionPlan as SubscriptionPlanType,
} from '@bugbug/core/types/subscriptions';
import type { Maybe } from '@bugbug/core/types/utils';
import useAppRoutes from '~/hooks/useAppRoutes';
import useModal from '~/hooks/useModal';
import {
  selectCurrentOrganizationId,
  selectSubscriptionSchedule,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import { ConfirmSubscriptionCancelModal } from '../ConfirmSubscriptionCancelModal/ConfirmSubscriptionCancelModal';
import { DowngradePlanModal } from '../DowngradePlanModal/DowngradePlanModal';
import { ParallelSelect } from '../ParallelSelect/ParallelSelect';
import { PlanFeature } from '../PlanFeature/PlanFeature';
import {
  PENDING_DOWNGRADE_INFO,
  SUBSCRIPTION_DETAILS,
} from '../Subscription/Subscription.constants';
import { isLowerPlan, isHigherPlan, isSamePlan } from '../Subscription/Subscription.helpers';
import { HelperIcon } from '../SubscriptionList/SubscriptionList.styled';

import * as S from './SubscriptionPlan.styled';

interface SubscriptionPlanProps {
  plan: SubscriptionPlanType;
  activeSubscription: Maybe<OrganizationSubscription>;
}

export const SubscriptionPlan = ({ plan, activeSubscription }: SubscriptionPlanProps) => {
  const { t } = useTranslation();
  const subscriptionSchedule = useAppSelector(selectSubscriptionSchedule);
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const appRoutes = useAppRoutes('subscription');
  const modal = useModal();
  const details = SUBSCRIPTION_DETAILS[plan.type];

  const activeParallelAddon = activeSubscription?.addons.find(
    (a) => a.type === 'extra_parallel_runs_limit',
  );
  const activeParallelRuns = activeParallelAddon?.value.toString() ?? '0';
  const [selectedParallelRuns, setSelectedParallelRuns] = useState(activeParallelRuns);

  const selectedParallelAddon = useMemo(
    () =>
      plan.possibleAddons.find(
        (addon): addon is ParallelSubscriptionAddonPlan =>
          addon.type === 'extra_parallel_runs_limit' &&
          addon.value === parseInt(selectedParallelRuns, 10),
      ),
    [plan.possibleAddons, selectedParallelRuns],
  );

  const totalPrice = plan.pricePerMonth.plus(selectedParallelAddon?.pricePerMonth ?? 0);
  const parallelDiff =
    (selectedParallelAddon?.value ?? 0) - (parseInt(activeParallelRuns, 10) ?? 0);

  const onTrial = activeSubscription?.isTrial ?? false;
  const isCurrentPlan = !onTrial && isSamePlan(plan, activeSubscription);
  const isCurrentPlanAndParallel = isCurrentPlan && parallelDiff === 0;
  const isFreeWhileOnTrial = plan.type === 'free' && onTrial;
  const isUpgrade =
    !isFreeWhileOnTrial &&
    (onTrial || isHigherPlan(plan, activeSubscription) || (isCurrentPlan && parallelDiff > 0));
  const isDowngrade =
    (!onTrial && isLowerPlan(plan, activeSubscription)) || (isCurrentPlan && parallelDiff < 0);

  const onPlanSelect = () => {
    if (isDowngrade) {
      analytics.trackEvent('downgrade_button_clicked', {
        [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
        [TRACK_EVENT_ARG_TYPE.ACCOUNT_PLAN]: activeSubscription?.type ?? 'free',
        [TRACK_EVENT_ARG_TYPE.PLAN_DOWNGRADED_TO]: plan.type,
        [TRACK_EVENT_ARG_TYPE.DOWNGRADE_TYPE]: isLowerPlan(plan, activeSubscription)
          ? 'plan_downgrade'
          : 'parallel_runs_downgrade',
      });
    }

    if (isUpgrade) {
      appRoutes.push('checkoutBillingAddress', undefined, {
        type: plan.type,
        billingCycle: plan.billingCycle,
        parallel: selectedParallelAddon?.value.toString() ?? '0',
      });
    } else if (activeSubscription && plan.type !== 'free') {
      modal.showCustom(() => (
        <DowngradePlanModal
          activeSubscription={activeSubscription}
          downgradePlan={plan}
          downgradeParallels={selectedParallelAddon?.value ?? 0}
        />
      ));
    } else {
      modal.showCustom(ConfirmSubscriptionCancelModal);
    }
  };

  const isDowngradeInProgress = subscriptionSchedule !== null;
  const hasFreePlan =
    !activeSubscription || activeSubscription.type === 'free' || activeSubscription.isTrial;

  return (
    <S.OuterWrapper key={plan.plan + plan.billingCycle}>
      {hasFreePlan && plan.type === 'pro' && (
        <S.Badge>{t('subscription.badge.mostPopular', 'Most popular')}</S.Badge>
      )}
      <S.PlanContainer>
        <div>
          <S.PlanTitle>{details.name}</S.PlanTitle>
          <S.PlanDescription>{details.description}</S.PlanDescription>
        </div>
        <div>
          <S.PlanPrice>${totalPrice.toString()}</S.PlanPrice>
          <S.PlanDescription>
            {plan.billingCycle === 'monthly' || plan.type === 'free'
              ? t('subscription.billingCycleDescription.monthly', 'per month')
              : t('subscription.billingCycleDescription.yearly', 'per month, billed annually')}
          </S.PlanDescription>
        </div>
        <S.MainFeaturesList>
          {details.mainFeatures.map((feature, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <PlanFeature key={`${plan.plan}-${index}-feature`} {...feature} />
          ))}
        </S.MainFeaturesList>
        <S.PlanForm>
          <Tooltip
            fitContent
            content={t(
              'subscription.parallelRuns.tooltip',
              'Run multiple tests simultaneously in the cloud—up to 32x faster.',
            )}
          >
            <S.PlanFormLabel>
              <span>{t('subscription.parallelRuns.label', 'Parallel runs')}</span>
              <HelperIcon />
            </S.PlanFormLabel>
          </Tooltip>
          <ParallelSelect
            plan={plan}
            initialParallelRuns={activeParallelRuns}
            selectedParallelRuns={selectedParallelRuns}
            setSelectedParallelRuns={setSelectedParallelRuns}
            variant="large"
          />
        </S.PlanForm>
        {isCurrentPlanAndParallel && (
          <S.CurrentPlan>
            <S.CurrentPlanIcon />
            <span>{t('subscription.currentPlan', 'Current plan')}</span>
          </S.CurrentPlan>
        )}
        {isFreeWhileOnTrial && (
          <S.CurrentPlan data-testid="FreePlanPlaceholder">
            <span>{t('subscription.freePlan', 'Free')}</span>
          </S.CurrentPlan>
        )}
        {!isFreeWhileOnTrial && isUpgrade && (
          <Tooltip content={isDowngradeInProgress && PENDING_DOWNGRADE_INFO}>
            <S.ManageSubscriptionButton
              variant="primary"
              onClick={onPlanSelect}
              disabled={isDowngradeInProgress}
            >
              <S.UpgradeIcon />
              <span>{t('subscription.upgrade', 'Upgrade')}</span>
            </S.ManageSubscriptionButton>
          </Tooltip>
        )}
        {isDowngrade && (
          <Tooltip content={isDowngradeInProgress && PENDING_DOWNGRADE_INFO}>
            <S.ManageSubscriptionButton
              variant="primary"
              onClick={onPlanSelect}
              disabled={isDowngradeInProgress}
            >
              <S.DowngradeIcon />
              <span>{t('subscription.downgrade', 'Downgrade')}</span>
            </S.ManageSubscriptionButton>
          </Tooltip>
        )}
        <S.FeaturesSection>
          <S.FeaturesTitle>{details.otherFeaturesTitle}</S.FeaturesTitle>
          <S.FeaturesList>
            {details.features.map((feature, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <PlanFeature key={`${plan.plan}-${index}-feature`} {...feature} />
            ))}
          </S.FeaturesList>
        </S.FeaturesSection>
      </S.PlanContainer>
    </S.OuterWrapper>
  );
};
